import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import isEqual from 'lodash/isEqual';

import {TIMEZONES} from 'common/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {ColorChoice, FunnelBookmark, FunnelSegment} from 'common/report/funnels/models/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {getMedianToolTipText} from 'common/report/funnels/models/time-to-convert/utils';
import {createTooltipDataCallback} from 'common/report/funnels/models/aggregates';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {ChartType, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {
  bookmarkHasTimeComparison,
  generateLegendSections,
  getBarChartDataFromRawFunnel,
  getDefaultSortConfig,
  sortSegments,
} from 'common/report/funnels/util/bar-chart';
import {getCalendarDateRangeLabels} from 'common/report/funnels/models/utils';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';

import css from './index.inline.styl';

interface TTCAggregateChartAttrs {
  'project-as-of-date': string;
  'project-timezone': typeof TIMEZONES[number];
}

interface TTCAggregateChartState {
  formattedData: FunnelSegment[];
  formattedCompareData: FunnelSegment[];
  selectedSegments: ColorChoice[];
  stepIndex: number;
  query: FunnelsQuery;
  dateRangeLabels: string[];
}

export class FunnelsTTCAggregateChart extends Component<
  TTCAggregateChartState,
  TTCAggregateChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;

  static get attrsSchema(): AttrsSchema<TTCAggregateChartAttrs> {
    return {
      ...super.attrsSchema,
      'project-as-of-date': `string`,
      'project-timezone': `string`,
    };
  }

  connectedCallback() {
    // Have to call processData after creation of the element in the DOM tree (so the context can be grabbed from parent),
    // but before first render
    this.processData();

    // TODO(sam): make funnels bind to the segment controller too, so that
    // url state/funnel query state updates when segments are toggled
    this.segmentFilterController.bindToComponent(this);

    super.connectedCallback();
  }

  disconnectedCallback() {
    this.segmentFilterController.unbindFromComponent(this);
    super.disconnectedCallback();
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config(): ConfigOptions<TTCAggregateChartState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.processData();
    }
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  private processData() {
    if (!(this._funnelsResponse && this._bookmark && this.isConnected)) {
      return;
    }

    const {sections, savedLegend} = generateLegendSections(this._funnelsResponse, this._bookmark);

    this.segmentFilterController.loadSections(sections, {
      defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTTCBar].default,
      maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTTCBar].max,
    });
    this.segmentFilterController.loadFromSavedLegend(savedLegend);

    this.update();
  }

  private renderTemplate() {
    const segmentFilterController = this.segmentFilterController;
    const selectedSections = segmentFilterController
      .getSections()
      .map((segmentSection: SegmentSection, i) => ({
        ...segmentSection,
        items: segmentSection.items
          .filter((v) => this.segmentFilterController.isItemSelected(i, v.id))
          .map((v) => ({...v, color: this.segmentFilterController.getItemColor(i, v.id)})),
      }))
      // Reverse is because we want the deepest segmentation to be the first in the list
      .reverse();

    const dateRangeLabels = getCalendarDateRangeLabels(
      this._bookmark,
      this._funnelsResponse,
      this.attr(`project-as-of-date`),
      this.attr(`project-timezone`),
    );

    const segments = getBarChartDataFromRawFunnel({
      funnelsResponse: this._funnelsResponse,
      selectedSections,
      bookmark: this._bookmark,
    });
    sortSegments(segments, this._funnelsResponse, this._bookmark, getDefaultSortConfig(selectedSections.length));

    return (
      <div sel='.ttc-aggregate-widget'>
        <BarVizEl
          attrs={{}}
          props={{
            chartData: segments,
            tooltipDataCallback: createTooltipDataCallback(
              bookmarkHasTimeComparison(this._bookmark),
              getMedianToolTipText,
              dateRangeLabels,
            ),
          }}
        />
      </div>
    );
  }
}

export const FunnelsTtcAggregateWidgetEl = defineAndMakeJsxFactory(
  `mp-funnels-ttc-aggregate-chart-widget`,
  FunnelsTTCAggregateChart,
);
export const FunnelsTtcAggregateWidgetReact = panel2react(
  `mp-funnels-ttc-aggregate-chart-widget`,
  FunnelsTTCAggregateChart,
);
